/*
  GANGSTER — scroll progress driver for [data-gs-scene] elements.
  Writes --e / --p / --t / --v (0..1) on scenes near the viewport; gs-story.css does the rest.
  One passive scroll listener, one requestAnimationFrame per frame, reads batched before writes.
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var supported =
    'IntersectionObserver' in window &&
    !!window.CSS &&
    CSS.supports('position', 'sticky') &&
    CSS.supports('opacity', 'clamp(0, calc(1 / 2), 1)') &&
    CSS.supports('clip-path', 'inset(0)');

  var VARS = ['--e', '--p', '--t', '--v'];
  var active = new Set();
  var settling = new Set();
  var observer = null;
  var queued = false;
  var enabled = false;
  var viewport = window.innerHeight;

  function clamp(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  function frame() {
    queued = false;
    var rects = [];
    active.forEach(function (el) {
      rects.push(el, el.getBoundingClientRect());
    });
    settling.forEach(function (el) {
      if (!active.has(el)) rects.push(el, el.getBoundingClientRect());
    });
    settling.clear();

    for (var i = 0; i < rects.length; i += 2) {
      var el = rects[i];
      var top = rects[i + 1].top;
      var height = rects[i + 1].height;
      var values = [
        clamp((viewport - top) / viewport),
        clamp(-top / Math.max(height - viewport, 1)),
        clamp((viewport - top) / Math.max(height, 1)),
        clamp((viewport - top) / (viewport + height)),
      ];
      var cache = el.gsValues || (el.gsValues = []);
      for (var j = 0; j < 4; j++) {
        var value = values[j].toFixed(4);
        if (cache[j] !== value) {
          cache[j] = value;
          el.style.setProperty(VARS[j], value);
        }
      }
    }
  }

  function request() {
    if (!queued) {
      queued = true;
      window.requestAnimationFrame(frame);
    }
  }

  function onResize() {
    viewport = window.innerHeight;
    request();
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        active.add(entry.target);
      } else {
        active.delete(entry.target);
        settling.add(entry.target);
      }
    });
    request();
  }

  function scan() {
    if (!enabled) return;
    observer.disconnect();
    active.clear();
    document.querySelectorAll('[data-gs-scene]').forEach(function (el) {
      observer.observe(el);
    });
    request();
  }

  function enable() {
    if (enabled || !supported || reduce.matches) return;
    enabled = true;
    viewport = window.innerHeight;
    root.classList.add('gs-motion');
    observer = new IntersectionObserver(onIntersect, { rootMargin: '25% 0px' });
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', onResize);
    scan();
  }

  function disable() {
    if (!enabled) return;
    enabled = false;
    root.classList.remove('gs-motion');
    observer.disconnect();
    observer = null;
    active.clear();
    settling.clear();
    window.removeEventListener('scroll', request);
    window.removeEventListener('resize', onResize);
    document.querySelectorAll('[data-gs-scene]').forEach(function (el) {
      el.gsValues = null;
      VARS.forEach(function (name) {
        el.style.removeProperty(name);
      });
    });
  }

  function sync() {
    if (reduce.matches) disable();
    else enable();
  }

  if (reduce.addEventListener) reduce.addEventListener('change', sync);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', function () {
    window.requestAnimationFrame(scan);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
